import { daysBetweenTwoDates } from '@/utils/date.utils'
import type { DataLayerObject } from '@gtm-support/vue-gtm'
import { useGtm } from '@gtm-support/vue-gtm'
import { defineStore } from 'pinia'
import { UAParser } from 'ua-parser-js'
import { useRouter, type RouteLocationNormalizedLoaded } from 'vue-router'
import type GTMPageType from '../models/GTMPageType'
import { GTMBranchesEnum } from '../models/enums'
import { useBusinessStore } from './businessStore'
import { useCommonStore } from './commonStore'
import { useSharedGtmDataStore } from './sharedGtmDataStore'

const uaParser = new UAParser()

export const useGTMStore = defineStore('gtmStore', () => {
  const gtm = useGtm()
  const commonStore = useCommonStore()
  const router = useRouter()
  const sharedGtmDataStore = useSharedGtmDataStore()
  const businessStore = useBusinessStore()

  function pageEvent(pageType: GTMPageType, _event?: string) {
    const currentRoute = router.currentRoute.value

    // Extraemos datos de dispositivo y sistema operativo con ua-parser-js
    const deviceInfo = uaParser.getDevice()
    const osInfo = uaParser.getOS()

    const rawTipo = deviceInfo.type
    const tipoDispositivo = rawTipo ? rawTipo.charAt(0).toUpperCase() + rawTipo.slice(1) : 'Desktop'

    const osName = osInfo.name ? osInfo.name.toUpperCase() : 'UNKNOWN'
    const resolution = `${window.screen.width}x${window.screen.height}`
    const modeloDetectado = `${tipoDispositivo};${osName};${resolution}`

    const num_solicitud =
      sharedGtmDataStore.hiringNumber ?? businessStore.getInsurance()?.quotationNumber ?? undefined

    sendEvent({
      event: 'page_view_custom',
      user_type: commonStore.getUserInfo().type,
      dispositivo: commonStore.getUserInfo().device,
      modelo_dispositivo: modeloDetectado,
      campaign_web: _checkCampaignWeb(currentRoute),
      grupo_usuario: 'particulares',
      promotion_name: undefined,
      url: window.location.origin + router.resolve(currentRoute).href,
      nombre_pagina: currentRoute.meta.title,
      page_type: pageType,
      producto: businessStore.getInsurance()?.productName ?? undefined,
      num_solicitud: num_solicitud ? String(num_solicitud) : undefined,
      value: sharedGtmDataStore.policyValue,
      ramo: sharedGtmDataStore.ramo ?? undefined
    })
  }

  function sendEvent(data: DataLayerObject) {
    if (gtm && gtm.enabled()) {
      const cod_mediador =
        (data as any).cod_mediador ?? commonStore.infoMediator?.mediatorId ?? null
      gtm.push({
        ...data,
        cod_mediador
      })
    }
  }

  function sendRaterOrHiringInteractionEvent(
    data: {
      event: string
      [k: string]: string | number | boolean | undefined | Object
    },
    answers?: any
  ) {
    const value = data.value ?? sharedGtmDataStore.policyValue
    let cobert_adicionales = data.cobert_adicionales ?? sharedGtmDataStore.additionalCoverages
    if (typeof cobert_adicionales === 'string') {
      cobert_adicionales =
        cobert_adicionales === 'yes' || cobert_adicionales === '1' || cobert_adicionales === 'true'
    }

    let tipo_viaje = undefined
    let destino_viaje = undefined
    let duracion_viaje = undefined

    if (data?.ramo === GTMBranchesEnum.TRAVEL) {
      tipo_viaje =
        data.tipo_viaje === 'null' || data.tipo_viaje === '' || data.tipo_viaje === null
          ? null
          : data.tipo_viaje ??
            sharedGtmDataStore.travel_type ??
            getGTMTravelType(answers?.hiring?.insuranceTypes[0])

      destino_viaje =
        data.destino_viaje ??
        sharedGtmDataStore.travel_destination ??
        getGTMTravelDestination(answers?.hiring?.destinationType)

      duracion_viaje =
        data.duracion_viaje ??
        sharedGtmDataStore.travel_duration ??
        (answers?.hiring && answers?.hiring?.departureDate && answers?.hiring?.returnDate
          ? daysBetweenTwoDates(
              new Date(answers?.hiring?.departureDate),
              new Date(answers?.hiring?.returnDate)
            )
          : undefined)
    }
    const num_solicitud = data?.num_solicitud ?? sharedGtmDataStore?.hiringNumber

    sendEvent({
      ...data,
      producto: data.producto,
      item_id: data.item_id ? String(data.item_id) : undefined,
      ramo: data.ramo ?? sharedGtmDataStore.ramo ?? undefined,
      value: value !== undefined ? Number(value) : value,
      num_asegurados: data.num_asegurados ?? sharedGtmDataStore.forWhomPeople,
      cobert_adicionales: cobert_adicionales ?? false,
      otra_comp: data.otra_comp ?? sharedGtmDataStore.anotherCompany ?? false,
      capital: data.capital ?? sharedGtmDataStore.capital,
      tipo_beneficiario:
        data.tipo_beneficiario || sharedGtmDataStore.beneficiaries_type || undefined,
      num_beneficiarios:
        data.num_beneficiarios || sharedGtmDataStore.beneficiaries_number || undefined,
      num_mascotas: data.num_mascotas ?? sharedGtmDataStore.forWhomPets,
      tipo_viaje: tipo_viaje || undefined,
      destino_viaje: destino_viaje || undefined,
      duracion_viaje: duracion_viaje || undefined,
      num_solicitud: num_solicitud ? String(num_solicitud) : undefined
    })
  }

  function _checkCampaignWeb(currentRoute: RouteLocationNormalizedLoaded) {
    const query = currentRoute.query
    if (query && Object.keys(query).length > 0) {
      return !!query['gclid'] || !!query['dclid']
    }
    return false
  }

  function getGTMTravelType(id: string | number): string {
    const travelTypes: Record<string, string> = {
      '6': 'Regular',
      '7': 'Crucero',
      '8': 'Varios',
      '9': 'Estudios'
    }
    return travelTypes[String(id)] ?? 'Regular'
  }

  function getGTMTravelDestination(id: string | number): string {
    const travelDestinations: Record<string, string> = {
      '1': 'España',
      '2': 'Europa',
      '3': 'Resto',
      '4': 'Europa'
    }
    return travelDestinations[String(id)] ?? 'España'
  }

  return {
    pageEvent,
    sendEvent,
    sendRaterOrHiringInteractionEvent,
    getGTMTravelType,
    getGTMTravelDestination
  }
})
